import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
const CHECKOUT_URL = 'https://l.instagram.com/?u=http%3A%2F%2Fwa.me%2F17868671817%3Futm_source%3Dig%26utm_medium%3Dsocial%26utm_content%3Dlink_in_bio%26fbclid%3DPAZXh0bgNhZW0CMTEAc3J0YwZhcHBfaWQMMjU2MjgxMDQwNTU4AAGnaqJ2Sof8kfKpMO62ETPquQzMk-2f5LDvIZ9MIKBRalOfvG9MYpFuYb5DDDE_aem_l3V7ISs1CoqqMmsUrca1pg&e=AT7ythc19Xwk1ZxDZqn7pj_Ell9adPiCk2-P9fqK616cnbmjzxEBcJ8ATXaG_tA4TMS_dwYUCoaPw3TvwftFSgyJXyIwu7Nn4YUvG2aIPQ';
interface HeroProps {
  onBookClick: () => void;
  onServicesClick: () => void;
}
export function Hero({
  onBookClick,
  onServicesClick
}: HeroProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  const contentOpacity = useTransform(smoothProgress, [0, 0.5], [1, 0]);
  const contentScale = useTransform(smoothProgress, [0, 0.5], [1, 0.95]);
  const contentY = useTransform(smoothProgress, [0, 0.5], [0, -50]);
  const imageScale = useTransform(smoothProgress, [0, 0.6], [1, 1.1]);
  const imageOpacity = useTransform(smoothProgress, [0, 0.6], [1, 0.3]);
  const bgY = useTransform(smoothProgress, [0, 1], [0, 100]);
  return <section ref={containerRef} className="relative min-h-screen flex items-center overflow-hidden" data-id="element-71">
      {/* Background decoration */}
      <motion.div style={{
      y: bgY
    }} className="absolute inset-0 z-0 pointer-events-none" data-id="element-72">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gold/[0.03] rounded-full blur-3xl -translate-y-1/3 -translate-x-1/4" data-id="element-73" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gold/[0.04] rounded-full blur-3xl translate-y-1/4 translate-x-1/4" data-id="element-74" />
      </motion.div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-24 md:py-0" data-id="element-75">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-screen lg:min-h-0 lg:py-32" data-id="element-76">
          {/* Text Content - Left Side */}
          <motion.div style={{
          opacity: contentOpacity,
          scale: contentScale,
          y: contentY
        }} className="flex flex-col order-2 lg:order-1 pb-8 lg:pb-0" data-id="element-77">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="flex flex-col" data-id="element-78">
              {/* Decorative line */}
              <motion.div initial={{
              width: 0
            }} animate={{
              width: 60
            }} transition={{
              delay: 0.6,
              duration: 0.8
            }} className="h-[1px] bg-gold mb-8" data-id="element-79" />

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-softBlack leading-[1.1] mb-6" data-id="element-80">
                Belleza natural, <br data-id="element-81" />
                <motion.span initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3,
                duration: 0.8
              }} className="italic text-warmGray" data-id="element-82">
                  cuidada con intención
                </motion.span>
              </h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="text-lg md:text-xl text-warmGray max-w-lg mb-10 font-light leading-relaxed" data-id="element-83">
                Tratamientos personalizados para realzar tu piel con técnica,
                calma y resultados visibles.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7,
              duration: 0.8
            }} className="flex flex-col sm:flex-row gap-4 items-start mb-10" data-id="element-84">
                <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-gold hover:bg-gold-dark text-white rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 min-w-[180px] text-center" data-id="element-85">
                  Agendar cita
                </a>
                <button onClick={onServicesClick} className="px-8 py-4 bg-transparent border border-gold text-gold hover:bg-gold/5 rounded-full font-medium transition-all duration-300 min-w-[180px]" data-id="element-86">
                  Ver servicios
                </button>
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1,
              duration: 0.8
            }} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-warmGray/70 tracking-wide uppercase" data-id="element-87">
                <span data-id="element-88">Atención personalizada</span>
                <span className="text-gold" data-id="element-89">·</span>
                <span data-id="element-90">Higiene impecable</span>
                <span className="text-gold" data-id="element-91">·</span>
                <span data-id="element-92">Resultados progresivos</span>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Image - Right Side */}
          <motion.div style={{
          scale: imageScale,
          opacity: imageOpacity
        }} className="relative order-1 lg:order-2 flex justify-center lg:justify-end" data-id="element-93">
            <motion.div initial={{
            clipPath: 'inset(100% 0% 0% 0%)'
          }} animate={{
            clipPath: 'inset(0% 0% 0% 0%)'
          }} transition={{
            duration: 1.2,
            ease: [0.25, 0.46, 0.45, 0.94],
            delay: 0.2
          }} className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl" data-id="element-94">
              <motion.div initial={{
              scale: 1.2,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 1.6,
              ease: [0.25, 0.46, 0.45, 0.94],
              delay: 0.2
            }} className="relative aspect-[3/4] rounded-2xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.1)]" data-id="element-95">
                <video src="https://cdn.magicpatterns.com/uploads/co2JMA1NrCHta8HYKGZQ2H/cindy video web.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-96" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent pointer-events-none" data-id="element-97" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 1.2,
              duration: 0.8
            }} className="absolute -inset-3 border border-gold/20 rounded-3xl pointer-events-none" data-id="element-98" />

              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 1.5,
              duration: 0.8
            }} className="absolute -left-6 top-1/3 w-12 h-[1px] bg-gold hidden lg:block" data-id="element-99" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div style={{
      opacity: useTransform(scrollYProgress, [0, 0.1], [1, 0])
    }} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.8,
      duration: 1
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10" data-id="element-100">
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} data-id="element-101">
          <ChevronDown className="text-gold/50 w-8 h-8" data-id="element-102" />
        </motion.div>
      </motion.div>
    </section>;
}